<template>
  <div class="table-container">
    <slot name="header" />
    <b-table
      ref="table"
      :items="items"
      :fields="fields"
      :hover="hover"
      :responsive="responsive"
      :sticky-header="stickyHeader"
      table-variant="light"
      tbody-tr-class="custom-table-row"
      :thead-class="theadClasses"
      :table-class="tableClasses"
      show-empty
      :selectable="selectable"
      :select-mode="selectMode"
      @row-selected="onRowSelected"
      no-select-on-click
      no-local-sorting
      @sort-changed="handleSortChange"
      :busy="loading"
    >
      <template #head(selection)>
        <div class="d-flex justify-content-center">
          <b-form-checkbox
            :disabled="!thereIsNoTasksForSelect"
            @change="handleSelectAll"
            v-model="selectAll"
          >
          </b-form-checkbox>
        </div>
      </template>

      <template #head(actions)="data">
        <div class="text-center">{{ data.label }}</div>
      </template>

      <template #cell(selection)="data">
        <div class="d-flex justify-content-center">
          <b-form-checkbox
            :disabled="!disableSelectionInRow(data.item.category)"
            :checked="data.rowSelected"
            @change="selectItem(data)"
          >
          </b-form-checkbox>
        </div>
      </template>

      <template #cell(actions)="data">
        <div
          @click="complete(data)"
          v-if="dismissAllowance(data)"
          class="complete-task-button cursor-pointer"
        >
          <i class="fa fa-check" style="color: #363ABB;"></i>
          <span class="text-button">Complete</span>
        </div>
      </template>

      <template #cell(description)="data">
        <div
          :data-todo-item="data.item.id"
          v-html-sanitize="data.item.description"
        ></div>
      </template>

      <template #table-busy>
        <div class="text-center text-primary my-4">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "CustomTable",
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    hover: { type: Boolean, default: true },
    responsive: { type: Boolean, default: false },
    stickyHeader: { type: [Boolean, String], default: "100%" },
    renderers: { type: Object, default: () => ({}) },
    dashedBorder: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    noLeftPadding: { type: Boolean, default: false },
    tableExtraClasses: { type: String, default: "" },
    selectable: { type: Boolean, default: true },
    selectMode: { type: String, default: "multi" },
    dismissionLevel: { type: String, default: "" },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      selected: [],
      selectAll: false
    };
  },
  methods: {
    onRowSelected(items) {
      this.selected = items;
    },
    selectAllRows() {
      for (let index = 0; index < this.items.length; index++) {
        const element = this.items[index];

        if (
          element.category === "Complete" ||
          element.category === "Sign & Complete"
        ) {
          this.selectRow(index);
        }
      }
    },
    clearSelected() {
      this.selectAll = false;
      this.$refs.table.clearSelected();
    },
    selectRow(index) {
      this.$refs.table.selectRow(index);
    },
    unselectRow(index) {
      this.$refs.table.unselectRow(index);
    },
    selectItem(data) {
      const index = data.index;
      if (data.rowSelected) {
        this.unselectRow(index);
      } else {
        this.selectRow(index);
      }
      if (this.selectAll) this.selectAll = false;
    },
    edit(item) {
      this.$emit("edit", item);
    },
    complete(item) {
      this.$emit("complete", item);
    },
    handleSortChange(data) {
      this.$emit("sortData", data);
    },
    dismissAllowance(item) {
      const complete = this.hasCurrentUserPermissionAccess(
        "others",
        "toDoItemsComplete"
      );
      if (item.item.category === "Manual To Do" || complete) {
        return true;
      }
    },
    handleSelectAll() {
      if (this.selectAll) {
        this.selectAllRows();
      } else {
        this.clearSelected();
      }
    },
    disableSelectionInRow(category) {
      return category === "Complete" || category === "Sign & Complete";
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserPermissionAccess"]),
    customSlots() {
      return this.fields.map(field => {
        let name = typeof field === "string" ? field : field.key;
        return [`cell(${name})`, this.renderers[name]];
      });
    },
    thereIsNoTasksForSelect() {
      return (
        this.items?.filter(
          item =>
            item.category === "Complete" || item.category === "Sign & Complete"
        ).length > 0
      );
    },
    tableClasses() {
      return `custom-table h-100 ${this.tableExtraClasses}`;
    },
    theadClasses() {
      let extraClass = this.hideHeader ? "d-none" : "";
      return `custom-table-head ${extraClass}`;
    }
  },
  watch: {
    selected(value) {
      this.$emit("input", [...value]);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .table {
  font-family: Bahnschrift;
  font-size: 14px;
  height: 666px;
}

::v-deep .custom-table-head th {
  font-size: 16px;
  font-weight: normal;
  background-color: white;
  padding-top: 7px;
  padding-bottom: 7px;
}
.custom-table-head th:first-child,
.custom-table-row td:first-child {
  /* padding-left: v-bind(firstColumnPadding); */
  /* padding-right: v-bind(firstColumnPadding); */
}

::v-deep .custom-table-row td {
  padding-top: 6px;
  padding-bottom: 6px;
  vertical-align: middle;
}

::v-deep .custom-table-head tr,
.custom-table-row {
  border-bottom: 1px rgba(0, 0, 0, 0.21);
  border-bottom-style: v-bind(borderStyle);
}

::v-deep .table.b-table > thead {
  color: #646a7a;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border-radius: 3px 3px 0px 0px !important;
}

.table-container {
  height: calc(100vh - 263px) !important;
  border-radius: 8px;
  background: var(--white, #fff);
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.17);
}

/* Customize the scrollbar for WebKit browsers */
::v-deep .table.b-table::-webkit-scrollbar {
  width: 2px !important; /* Set the width of the scrollbar */
}

::v-deep .table.b-table::-webkit-scrollbar-thumb {
  background-color: #363abb; /* Set the color of the scrollbar thumb */
  border-radius: 6px; /* Round the edges of the thumb */
  border: 2px solid #363abb;
}

::v-deep .table.b-table::-webkit-scrollbar-thumb:hover {
  background-color: #363abb; /* Change thumb color on hover */
}

::v-deep .dismiss {
  color: #363abb;
  font-family: Bahnschrift;
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: normal;
  text-decoration-line: underline;
}

::v-deep .b-table-sticky-header > .table.b-table > thead > tr > th {
  position: sticky !important;
}

.complete-task-button {
  display: flex;
  padding: 5px 10.171px;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  border-radius: 3px;
  border: 0.424px solid #363abb;
  box-shadow: 0px 0px 0.5px 0px rgba(54, 58, 187, 0.5);
  height: 35px;
}

.complete-task-button:hover {
  background: #363abb;

  span,
  i {
    color: #ffff !important;
  }
}

.text-button {
  color: #363abb;
  font-family: Bahnschrift;
  font-size: 11.867px;
  font-style: normal;
  font-weight: 350;
  line-height: normal;
}

::v-deep .column {
  &-date {
    width: 100px;
  }

  &-patient {
    max-width: 212px;
  }

  &-description {
    max-width: 450px;
  }

  &-category {
    width: 120px;
  }

  &-assignedTo {
    max-width: 258px;
  }

  &-actions {
    width: 120px;
  }
}

@media (max-width: 1500px) {
  .custom-table {
    width: calc(100vw - 91px) !important;
  }
}
@media (max-width: 1499px) {
  .table-container {
    height: calc(100vh - 315px) !important;
  }
}
</style>
